// ABOUTME: React Query hooks for admin user management operations
// ABOUTME: Provides hooks for listing users, creating users, and deactivating users

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import type { CreateUserRequest, ChangeRoleRequest } from '@/types/admin'

/**
 * Fetch all users (admin-only)
 */
export const useUsers = () => {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminApi.listUsers(),
  })
}

/**
 * Create a new user (admin-only)
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateUserRequest) => adminApi.createUser(data),
    onSuccess: () => {
      // Invalidate users list to refetch
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/**
 * Deactivate a user (admin-only)
 */
export const useDeactivateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) => adminApi.deactivateUser(userId),
    onSuccess: () => {
      // Invalidate users list to refetch
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/**
 * Reset a user's password (admin-only)
 */
export const useResetPassword = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: number) => adminApi.resetPassword(userId),
    onSuccess: () => {
      // Invalidate users list to refetch
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/**
 * Change a user's role (admin-only)
 */
export const useChangeUserRole = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) =>
      adminApi.changeUserRole(userId, { role } as ChangeRoleRequest),
    onSuccess: () => {
      // Invalidate users list to refetch
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}
